/**
 * @file use-manual-payment-form-state.ts
 *
 * Controlled-form state for the Manual payment form, mirroring
 * `use-settings-form-state.ts`. Holds the editable fields, exposes a `patch`
 * updater, and validates the required method name on save (errors surface only
 * after the first `validate()`). Re-seeds when the seed reference changes (the
 * screen passes a fresh seed once the gateway loads).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type {
  ManualPaymentFormErrors,
  ManualPaymentFormFields,
} from './manual-payment-form-types';

/** What {@link useManualPaymentFormState} returns to the form. */
export interface UseManualPaymentFormStateResult {
  fields: ManualPaymentFormFields;
  patch: (update: Partial<ManualPaymentFormFields>) => void;
  errors: ManualPaymentFormErrors;
  validate: () => boolean;
  /** Attach to the name field; focused/scrolled to when validation fails on it. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the Manual payment form's field state and validation.
 *
 * @param seed - The initial fields, derived from the loaded gateway; re-seeds the
 *   form when its reference changes.
 *
 * @returns The fields, updater, errors, and a `validate` function.
 */
export function useManualPaymentFormState(
  seed: ManualPaymentFormFields,
): UseManualPaymentFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<ManualPaymentFormFields>(seed);
  const seededRef = useRef(seed);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (seededRef.current === seed) {
      return;
    }
    seededRef.current = seed;
    setFields(seed);
    setSubmitted(false);
  }, [seed]);

  const patch = useCallback((update: Partial<ManualPaymentFormFields>) => {
    setFields((prev) => ({ ...prev, ...update }));
  }, []);

  const computeErrors = useCallback(
    (current: ManualPaymentFormFields): ManualPaymentFormErrors => {
      const errors: ManualPaymentFormErrors = {};
      if (current.title.trim() === '') {
        errors.titleError = intl.formatMessage(adminMessages.paymentsManualNameRequired);
      }
      // Description maps to the wire `info`, which the engine requires (min 1). Blocking
      // an empty value here turns a generic save-error toast into an inline field error.
      if (current.description.trim() === '') {
        errors.descriptionError = intl.formatMessage(
          adminMessages.paymentsManualDescriptionRequired,
        );
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid && result.titleError) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  return { fields, patch, errors, validate, firstErrorRef };
}
